import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SharedFooter from '../components/SharedFooter';
import { 
  Bath, Waves, ThermometerSun, ChevronLeft, Phone, Mail, MapPin,
  Check, Star, Sparkles, ArrowRight, Menu, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SpaPage = () => {
  const [content, setContent] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/site-content`);
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const parseList = (text) => {
    if (!text) return [];
    return text.split('|').filter(item => item.trim());
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="animate-pulse font-heading text-2xl text-forest">Ładowanie...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand" data-testid="spa-page">
      {/* Navigation - consistent with homepage */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src={content.site_logo || "https://customer-assets.emergentagent.com/job_rustic-retreat-7/artifacts/gtmkak5t_5556a.png"}
                alt="VIP Village" 
                className="h-24 md:h-28 w-auto object-contain"
              />
              <div className="text-forest">
                <span className="font-heading text-xl font-semibold">VIP Village</span>
                <p className="text-xs font-body opacity-80">Las • Woda • Rower</p>
              </div>
            </Link>
            
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-6">
              <a href="/#about" className="font-body text-sm text-forest hover:text-terracotta transition">O nas</a>
              <a href="/#offer" className="font-body text-sm text-forest hover:text-terracotta transition">Oferta</a>
              <a href="/#gallery" className="font-body text-sm text-forest hover:text-terracotta transition">Galeria</a>
              <Link to="/spa" className="font-body text-sm text-terracotta transition">Balia Basen Sauna</Link>
              <a href="/#attractions" className="font-body text-sm text-forest hover:text-terracotta transition">W okolicy</a>
              <a href="/#contact" className="font-body text-sm text-forest hover:text-terracotta transition">Kontakt</a>
              <Link to="/regulamin" className="font-body text-sm text-forest hover:text-terracotta transition">Regulamin</Link>
            </div>
            
            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-forest p-2"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <a href="/#about" className="block font-body text-forest py-2" onClick={() => setMobileMenuOpen(false)}>O nas</a>
              <a href="/#offer" className="block font-body text-forest py-2" onClick={() => setMobileMenuOpen(false)}>Oferta</a>
              <Link to="/spa" className="block font-body text-terracotta py-2" onClick={() => setMobileMenuOpen(false)}>Balia Basen Sauna</Link>
              <a href="/#contact" className="block font-body text-forest py-2" onClick={() => setMobileMenuOpen(false)}>Kontakt</a>
              <Link to="/regulamin" className="block font-body text-forest py-2" onClick={() => setMobileMenuOpen(false)}>Regulamin</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-20 md:pt-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-forest/20 to-transparent z-10" />
        <div 
          className="h-[50vh] md:h-[60vh] bg-cover bg-center relative"
          style={{ backgroundImage: `url(${content.balia_image || 'https://static.wixstatic.com/media/9ecdfe_7d76c9c4dd3c4503a3f094ba70074451~mv2.jpg'})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-forest/90 via-forest/40 to-transparent" />
          <div className="absolute inset-0 flex items-end z-20">
            <div className="max-w-7xl mx-auto px-4 pb-12 md:pb-16 w-full">
              <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-4 transition">
                <ChevronLeft className="w-4 h-4" /> Powrót do strony głównej
              </Link>
              <h1 className="font-heading text-4xl md:text-6xl text-white mb-4">
                {content.spa_page_title || "Balia • Basen • Sauna"}
              </h1>
              <p className="font-body text-lg md:text-xl text-white/80 max-w-2xl">
                {content.spa_page_intro || "Odkryj strefę wellness w VIP Village"}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="bg-forest py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4 md:gap-8">
            <a href="#balia" className="flex items-center gap-2 text-white hover:text-terracotta transition font-body">
              <Bath className="w-5 h-5" /> Gorąca Balia
            </a>
            <a href="#basen" className="flex items-center gap-2 text-white hover:text-terracotta transition font-body">
              <Waves className="w-5 h-5" /> Basen Premium
            </a>
            <a href="#sauna" className="flex items-center gap-2 text-white hover:text-terracotta transition font-body">
              <ThermometerSun className="w-5 h-5" /> Sauna Fińska
            </a>
          </div>
        </div>
      </section>

      {/* Balia Section */}
      <section id="balia" className="py-16 md:py-24 bg-sand">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 bg-terracotta/10 text-terracotta px-4 py-2 rounded-full mb-6">
                <Bath className="w-5 h-5" />
                <span className="font-body font-medium">{content.balia_subtitle || "Hot Tube z hydromasażem"}</span>
              </div>
              <h2 className="font-heading text-3xl md:text-5xl text-forest mb-6">
                {content.balia_title || "Gorąca Balia"}
              </h2>
              <p className="font-body text-lg text-forest/80 mb-6 leading-relaxed">
                {content.balia_description || "Balia mieści max. 6-8 osób..."}
              </p>
              <p className="font-body text-forest/70 mb-8 leading-relaxed">
                {content.balia_history || "Balie znane są od dawnych lat..."}
              </p>
              
              {/* Benefits */}
              <div className="space-y-3 mb-8">
                <h4 className="font-heading text-lg text-forest">Korzyści zdrowotne:</h4>
                {parseList(content.balia_benefits).map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-terracotta mt-0.5 flex-shrink-0" />
                    <span className="font-body text-forest/80">{benefit}</span>
                  </div>
                ))}
              </div>
              
              {/* Price Card */}
              <Card className="border-0 bg-forest text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-body text-white/70 mb-1">Cennik</p>
                      <p className="font-heading text-2xl whitespace-pre-line">
                        {content.balia_full_price || "190 zł / sesja"}
                      </p>
                    </div>
                    <a href={`tel:${content.phone || '+48605737885'}`}>
                      <Button className="bg-terracotta hover:bg-terracotta-dark gap-2">
                        <Phone className="w-4 h-4" /> Rezerwuj
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="relative">
                <img 
                  src={content.balia_image || "https://static.wixstatic.com/media/9ecdfe_7d76c9c4dd3c4503a3f094ba70074451~mv2.jpg"}
                  alt="Gorąca Balia"
                  className="w-full h-[400px] md:h-[500px] object-cover rounded-2xl shadow-elevated"
                />
                <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-soft hidden md:block">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-terracotta/10 rounded-full flex items-center justify-center">
                      <Star className="w-6 h-6 text-terracotta" />
                    </div>
                    <div>
                      <p className="font-heading text-2xl text-forest">40°C</p>
                      <p className="font-body text-sm text-forest/60">temperatura wody</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Basen Section */}
      <section id="basen" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="relative">
                <img 
                  src={content.basen_image || "https://static.wixstatic.com/media/9ecdfe_452fb696dd1942e683ba9fc7e796137d~mv2.jpg"}
                  alt="Basen Premium"
                  className="w-full h-[400px] md:h-[500px] object-cover rounded-2xl shadow-elevated"
                />
                <div className="absolute -bottom-6 -right-6 bg-terracotta text-white p-4 rounded-xl shadow-soft hidden md:block">
                  <p className="font-heading text-xl">W cenie pobytu!</p>
                  <p className="font-body text-sm text-white/80">maj - listopad</p>
                </div>
              </div>
            </div>
            
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full mb-6">
                <Waves className="w-5 h-5" />
                <span className="font-body font-medium">{content.basen_subtitle || "Basen zewnętrzny"}</span>
              </div>
              <h2 className="font-heading text-3xl md:text-5xl text-forest mb-6">
                {content.basen_title || "Basen Premium"}
              </h2>
              <p className="font-body text-lg text-forest/80 mb-8 leading-relaxed">
                {content.basen_description || "Podgrzewany basen dostępny od maja do listopada..."}
              </p>
              
              {/* Features */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                {parseList(content.basen_features).map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-sand/50 p-3 rounded-lg">
                    <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span className="font-body text-sm text-forest">{feature}</span>
                  </div>
                ))}
              </div>
              
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-xl">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-soft">
                    <Waves className="w-7 h-7 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-heading text-xl text-forest">Dla wszystkich gości</p>
                    <p className="font-body text-forest/70">Basen dostępny w ramach pobytu</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sauna Section */}
      <section id="sauna" className="py-16 md:py-24 bg-sand">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full mb-6">
                <ThermometerSun className="w-5 h-5" />
                <span className="font-body font-medium">{content.sauna_subtitle || "Sauna sucha"}</span>
              </div>
              <h2 className="font-heading text-3xl md:text-5xl text-forest mb-6">
                {content.sauna_title || "Sauna Fińska"}
              </h2>
              <p className="font-body text-lg text-forest/80 mb-8 leading-relaxed">
                {content.sauna_description || "Sauna sucha zbudowana z drewna..."}
              </p>
              
              {/* Benefits */}
              <div className="space-y-3 mb-8">
                <h4 className="font-heading text-lg text-forest">Zalety korzystania z sauny:</h4>
                {parseList(content.sauna_benefits).map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <span className="font-body text-forest/80">{benefit}</span>
                  </div>
                ))}
              </div>
              
              {/* Price Card */}
              <Card className="border-0 bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-body text-white/80 mb-1">Cennik</p>
                      <p className="font-heading text-xl whitespace-pre-line">
                        {content.sauna_full_price || "170 zł / 2h"}
                      </p>
                    </div>
                    <a href={`tel:${content.phone || '+48605737885'}`}>
                      <Button className="bg-white text-amber-600 hover:bg-white/90 gap-2">
                        <Phone className="w-4 h-4" /> Rezerwuj
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="relative">
                <img 
                  src={content.sauna_image || "https://static.wixstatic.com/media/9ecdfe_d25639c3083b4c3d82765d210d7cc046~mv2.jpg"}
                  alt="Sauna Fińska"
                  className="w-full h-[400px] md:h-[500px] object-cover rounded-2xl shadow-elevated"
                />
                <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-soft hidden md:block">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                      <ThermometerSun className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-heading text-2xl text-forest">80-110°C</p>
                      <p className="font-body text-sm text-forest/60">temperatura</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-forest">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
            Zarezerwuj swoją sesję wellness
          </h2>
          <p className="font-body text-lg text-white/70 mb-8">
            Skontaktuj się z nami, aby zarezerwować balię lub saunę podczas Twojego pobytu
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${content.phone || '+48605737885'}`}>
              <Button size="lg" className="bg-terracotta hover:bg-terracotta-dark text-white gap-2 w-full sm:w-auto">
                <Phone className="w-5 h-5" />
                {content.phone || "+48 605 737 885"}
              </Button>
            </a>
            <a href={`mailto:${content.email || 'agromiedzierza@gmail.com'}`}>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 gap-2 w-full sm:w-auto">
                <Mail className="w-5 h-5" />
                Napisz do nas
              </Button>
            </a>
          </div>
        </div>
      </section>

      <SharedFooter content={content} />
    </div>
  );
};

export default SpaPage;
